package org.lennycodes.gradematewebsite.components.style
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.autoLength
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.forms.CheckedState.Companion.from
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.selectors.hover
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.deg
import org.jetbrains.compose.web.css.px

// ---------------------------------------------------------------------------
// Theme
// ---------------------------------------------------------------------------



object GradeMateColors {
    val Primary = Color.rgb(25, 118, 210)
    val PrimaryDark = Color.rgb(17, 82, 147)
    val Secondary = Color.rgb(102, 187, 106)
    val Background = Color.rgb(245, 245, 245)
    val Surface = Color.rgb(255, 255, 255)
    val TextPrimary = Color.rgb(28, 30, 33)
    val TextSecondary = Color.rgb(92, 101, 112)
    val Border = Color.rgb(225, 228, 232)

    const val Primary25 = "rgba(25, 118, 210, 0.25)"
    const val Border35 = "rgba(225, 228, 232, 0.35)"
    val FooterBackground = Color.rgb(15, 27, 45)
    val TextOnDark = Color.rgb(232, 236, 242)
    val TextOnDarkSecondary = Color.rgb(139, 152, 172)
}

fun Color.toRgba(alpha: Float = 1f): String {
    val rgb = this.toRgb()
    return "rgba(${rgb.red}, ${rgb.green}, ${rgb.blue}, $alpha)"
}
private val ContentMaxWidth = 1120.px

// ---------------------------------------------------------------------------
// Shared / layout styles — base = phone, Breakpoint.MD+ = tablet/desktop
// ---------------------------------------------------------------------------

val SectionStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .padding(topBottom = 56.px, leftRight = 20.px)
    }
    Breakpoint.MD {
        Modifier.padding(topBottom = 96.px, leftRight = 32.px)
    }
}

val SectionInnerStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .maxWidth(ContentMaxWidth)
            .margin(leftRight = autoLength)
    }
}

// A two-column layout that stacks to one column on phones and becomes a row at MD+.
val ResponsiveRowStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .styleModifier {
                property("display", "flex")
                property("flex-direction", "column")
                property("gap", "40px")
            }
    }
    Breakpoint.MD {
        Modifier.styleModifier {
            property("flex-direction", "row")
            property("justify-content", "space-between")
            property("align-items", "center")
            property("gap", "48px")
        }
    }
}

// A responsive card grid: 1 column on phones, 2 on tablets, N on desktop.
private fun gridStyle(desktopColumns: Int) = CssStyle {
    base {
        Modifier.styleModifier {
            property("display", "grid")
            property("grid-template-columns", "1fr")
            property("gap", "20px")
        }
    }
    Breakpoint.SM {
        Modifier.styleModifier { property("grid-template-columns", "repeat(2, 1fr)") }
    }
    Breakpoint.LG {
        Modifier.styleModifier { property("grid-template-columns", "repeat($desktopColumns, 1fr)") }
    }
}

val QuickStartGridStyle = gridStyle(desktopColumns = 4)
val DownloadsGridStyle = gridStyle(desktopColumns = 3)

// Stacked full-width buttons on phones; side-by-side auto-width buttons at MD+.
val ButtonGroupStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .styleModifier {
                property("display", "flex")
                property("flex-direction", "column")
                property("gap", "12px")
            }
    }
    Breakpoint.MD {
        Modifier.styleModifier {
            property("flex-direction", "row")
            property("gap", "14px")
        }
    }
}

val EyebrowStyle = CssStyle {
    base {
        Modifier
            .color(GradeMateColors.Primary)
            .fontSize(13.px)
            .fontWeight(FontWeight.SemiBold)
    }
}

val SectionHeadingStyle = CssStyle {
    base {
        Modifier
            .color(GradeMateColors.TextPrimary)
            .fontSize(26.px)
            .fontWeight(FontWeight.Bold)
            .lineHeight(1.25)
    }
    Breakpoint.MD {
        Modifier.fontSize(38.px)
    }
}

val SectionSubtitleStyle = CssStyle {
    base {
        Modifier
            .color(GradeMateColors.TextSecondary)
            .fontSize(15.px)
            .lineHeight(1.6)
            .maxWidth(560.px)
    }
    Breakpoint.MD {
        Modifier.fontSize(17.px)
    }
}

val PrimaryButtonStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .backgroundColor(GradeMateColors.Primary)
            .color(Colors.White)
            .fontWeight(FontWeight.SemiBold)
            .fontSize(15.px)
            .padding(topBottom = 15.px, leftRight = 26.px)
            .borderRadius(10.px)
            .styleModifier { property("border", "none") }
            .cursor(Cursor.Pointer)
            .styleModifier { property("transition", "background-color 0.18s ease, transform 0.18s ease") }
    }
    Breakpoint.MD {
        Modifier.width(autoLength).padding(topBottom = 14.px, leftRight = 26.px)
    }
    hover {
        Modifier
            .backgroundColor(GradeMateColors.PrimaryDark)
            .styleModifier { property("transform", "translateY(-1px)") }
    }
    focusVisible {
        Modifier.styleModifier {
            property(
                "outline",
                "3px solid rgba(25, 118, 210, 0.35)"
            )
            property("outline-offset", "2px")
        }
    }
}

val SecondaryButtonStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .backgroundColor(GradeMateColors.Surface)
            .color(GradeMateColors.TextPrimary)
            .fontWeight(FontWeight.SemiBold)
            .fontSize(15.px)
            .padding(topBottom = 14.px, leftRight = 25.px)
            .borderRadius(10.px)
            .styleModifier {
                property("border", "1px solid #E1E4E8")
            }
            .cursor(Cursor.Pointer)
            .styleModifier { property("transition", "border-color 0.18s ease, transform 0.18s ease") }
    }
    Breakpoint.MD {
        Modifier.width(autoLength)
    }
    hover {
        Modifier
            .styleModifier {
                property("border-color", GradeMateColors.Primary.toString())
            }
            .styleModifier { property("transform", "translateY(-1px)") }
    }
    focusVisible {
        Modifier.styleModifier {
            property(
                "outline",
                "3px solid ${GradeMateColors.Primary25}"
            )
            property("outline-offset", "2px")
        }
    }
}

val CardStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(GradeMateColors.Surface)
            .borderRadius(16.px)
            .padding(22.px)
            .styleModifier {
                property(
                    "border",
                    "1px solid ${GradeMateColors.Border}"
                )
            }
    }
    Breakpoint.MD {
        Modifier.padding(28.px)
    }
}

val NavLinkStyle = CssStyle {
    base {
        Modifier
            .color(GradeMateColors.TextSecondary)
            .fontSize(15.px)
            .fontWeight(FontWeight.Medium)
    }
    hover { Modifier.color(GradeMateColors.TextPrimary) }
}

// Full link row: hidden on phones, shown from MD up.
val DesktopNavLinksStyle = CssStyle {
    base { Modifier.display(DisplayStyle.None) }
    Breakpoint.MD {
        Modifier.styleModifier {
            property("display", "flex")
            property("align-items", "center")
            property("gap", "28px")
        }
    }
}

// Hamburger button: shown on phones, hidden from MD up.
val MobileMenuButtonStyle = CssStyle {
    base {
        Modifier
            .display(DisplayStyle.Flex)
            .cursor(Cursor.Pointer)
            .padding(8.px)
    }
    Breakpoint.MD { Modifier.display(DisplayStyle.None) }
}






// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------

val FooterStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .backgroundColor(GradeMateColors.FooterBackground)
            .padding(topBottom = 28.px, leftRight = 20.px)
            .styleModifier {
                property("display", "flex")
                property("flex-direction", "column")
                property("gap", "16px")
            }
    }
    Breakpoint.MD {
        Modifier.padding(topBottom = 32.px, leftRight = 32.px).styleModifier {
            property("flex-direction", "row")
            property("justify-content", "space-between")
            property("align-items", "center")
        }
    }
}

// Separate from NavLinkStyle on purpose — the sticky top nav needs dark text on a
// light background, the footer needs the opposite, and reusing one style for both
// would mean every future nav tweak risks silently breaking the footer (or vice versa).
val FooterLinkStyle = CssStyle {
    base {
        Modifier
            .color(GradeMateColors.TextOnDarkSecondary)
            .fontSize(15.px)
            .fontWeight(FontWeight.Medium)
    }
    hover { Modifier.color(GradeMateColors.TextOnDark) }
}
val SkeletonStyle = CssStyle {
    base {
        Modifier
            .styleModifier {
                property(
                    "background",
                    "linear-gradient(" +
                            "90deg, " +
                            "rgba(15, 23, 42, 0.055) 0%, " +
                            "rgba(15, 23, 42, 0.10) 50%, " +
                            "rgba(15, 23, 42, 0.055) 100%"
                )
                property("background-size", "200% 100%")
                property(
                    "animation",
                    "grademate-skeleton-shimmer 1.8s ease-in-out infinite"
                )
            }
    }
}
// Top level property
// Top of file, alongside other top-level declarations
val WobbleKeyframes = Keyframes {
    from { Modifier.rotate((-5).deg) }
    to { Modifier.rotate(5.deg) }
}


val HighlightTileGridStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .styleModifier {
                property("display", "grid")
                // Single column on phones
                property("grid-template-columns", "1fr")
                property("gap", "14px")
            }
    }
    Breakpoint.SM {
        Modifier.styleModifier {
            // Two columns on small tablets
            property("grid-template-columns", "repeat(2, minmax(0, 1fr))")
        }
    }
    Breakpoint.LG {
        Modifier.styleModifier {
            // Four columns on desktop — 4×2 grid for 8 tiles
            property("grid-template-columns", "repeat(4, minmax(0, 1fr))")
        }
    }
}